import React, {
  useMemo,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import compact from 'lodash/compact';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Controller from 'core/components/FormController';
import {
  TemplateFieldDTO,
} from 'dtos';
import Button from 'pages/Dashboard/components/Button';
import BaseDialog from 'pages/Dashboard/components/Dialog';
import Input from 'pages/Dashboard/components/Input';
import Textarea from 'pages/Dashboard/components/Input/Textarea';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  notEmptyString,
  getSubmitButtonText,
} from '@populateui/utils';
import {
  useUpsertField,
} from 'views/EMR/Settings/hooks';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import ReadonlyNote from 'views/EMR/Settings/VisitNotes/ReadonlyNote';

type Field = Omit<TemplateFieldDTO, 'options'> & {
  options?: string;
};

export type FieldDialogParams = {
  data?: TemplateFieldDTO;
  onFieldCreated?: (field: TemplateFieldDTO) => void;
};

type Props = FieldDialogParams & {
  onClose: () => void;
};
export default function FieldDialog({
  data,
  onFieldCreated,
  onClose,
}: Props) {
  const isNew = isNil(data?.templateFieldId);
  const userInfo = useUserInfo();
  const editable = userInfo?.user?.userId === data?.userId || isNew;
  const { mutateAsync: upsertField } = useUpsertField();

  const formProps = useForm<Field>({
    mode: 'onChange',
    defaultValues: isNil(data)
      ? undefined
      : {
        ...omit(data, ['options']),
        options: data?.options?.join?.('\n'),
      },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, isSubmitting },
  } = formProps;

  const onSubmit = async (formData: Field) => {
    const { options, ...data } = formData;
    try {
      const field = await upsertField({ ...data, options: compact((options ?? '').split?.('\n')) });
      settingsEventChannel.emit('onVisitNoteSettingsChange', 'fields', field);
      onFieldCreated?.(field);
      onClose?.();
    } finally {
      reset(formData, { keepIsSubmitted: false });
    }
  };

  const submitButtonText = useMemo(() => getSubmitButtonText(
    isNew,
    isSubmitting,
  ), [isNew, isSubmitting]);

  const editTitle = editable ? 'Edit ' : 'View ';

  return (
    <BaseDialog
      title={(
        <div className="flex flex-1 flex-col space-y-2">
          <h2 className="text-lg font-semibold">
            {isNew ? 'Add ' : editTitle}
            Field
          </h2>
        </div>
      )}
      closeBtnClasses="!-top-1"
      onClose={onClose}
      onConfirm={noop}
      className="!w-[40rem] populate-form"
      maxWidth="lg"
      open
    >
      <FormProvider {...formProps}>
        <div className="flex flex-col gap-form-row w-full">
          <Controller
            name="title"
            control={control}
            rules={{ required: 'Field Title is required', validate: notEmptyString }}
            render={({ field, fieldState: { invalid } }) => (
              <Wrapper
                id="field-title"
                required={editable}
                containerClassName="flex gap-form-field items-end"
                label="Title"
                error={invalid}
                className="w-full"
              >
                <Input
                  id="field-title"
                  className="w-full"
                  placeholder="Enter field title..."
                  disabled={!editable}
                  {...field}
                />
              </Wrapper>
            )}
          />
          <Controller
            control={control}
            name="options"
            rules={{ required: 'Field Options are required', validate: notEmptyString }}
            render={({ field, fieldState: { invalid } }) => (
              <Textarea
                {...field}
                required={editable}
                label="Options"
                id="options"
                error={invalid}
                placeholder="Use break to separate options"
                disabled={!editable}
              />
            )}
          />
        </div>
        <div className="flex justify-between items-center w-full mt-4">
          <ReadonlyNote readonly={!editable} />
          <div className="flex-1 flex justify-end gap-3">
            <Button
              variant="outlined"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
            {editable && (
            <DisabledFormTooltip>
              <Button
                variant="contained"
                type="submit"
                color="primary"
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || isSubmitting}
              >
                {submitButtonText}
              </Button>
            </DisabledFormTooltip>
            )}
          </div>
        </div>
      </FormProvider>
    </BaseDialog>
  );
}
